import { IconChevronRight } from "@/assets/icons/geist/IconChevronRight.tsx";
import { IconLaptop } from "@/assets/icons/geist/IconLaptop.tsx";
import { Banner } from "@/components/flexkit/Banner";
import { Button } from "@/components/flexkit/Button.tsx";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/flexkit/Collapsible";
import { Link } from "@/components/flexkit/Link";
import { cn } from "@/components/podkit/lib/cn.ts";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { Heading3 } from "@/components/podkit/typography/Headings.tsx";
import { Text } from "@/components/podkit/typography/Text";
import { useProviders } from "@/components/runners/details/source-control-providers.tsx";
import { SourceControlProvider } from "@/components/runners/details/SourceControlProvider.tsx";
import { RunnerUsageButtons } from "@/components/runners/RunnerUsageButtons";
import { useDesktop } from "@/hooks/use-desktop";
import { useLocalRunner } from "@/hooks/use-local-runner";
import { useMembership } from "@/hooks/use-membership.ts";
import { TrackLocations } from "@/hooks/use-segment.ts";
import { type PlainRunner, useLocalConfigurationRunner } from "@/queries/runner-queries";
import { LocalRunnerStatus } from "frontend-shared/local-runner";
import { OrganizationRole } from "gitpod-next-api/gitpod/v1/organization_pb.ts";
import { type RunnerConfigurationSchema_SCMConfigSchema } from "gitpod-next-api/gitpod/v1/runner_configuration_pb.ts";
import { type FC, useMemo, useState } from "react";

export const LocalRunnerSettings: FC = () => {
    const { data: localConfigRunner, isPending: isPendingLocalConfigRunner } = useLocalConfigurationRunner();

    const { membership, isPending: isLoadingMembership } = useMembership();
    const ready = !isLoadingMembership && !isPendingLocalConfigRunner;
    const readOnly = !ready || membership?.userRole !== OrganizationRole.ADMIN;

    const { isDesktop } = useDesktop();
    const localRunner = useLocalRunner();

    if (!ready) {
        return <LoadingState />;
    }

    if (!localConfigRunner) {
        return null;
    }

    const localRunnerIsFailed = LocalRunnerStatus.isFailed(localRunner.status);

    return (
        <div data-testid="local-runner-settings" className="flex flex-col gap-6">
            <Heading3>Ona Desktop</Heading3>
            {isDesktop && localRunnerIsFailed && (
                <Banner
                    text={
                        <div>
                            There appears to be an issue with Ona Desktop.{" "}
                            <Link to={"/local-runner"}>Please check the status</Link>
                        </div>
                    }
                    variant="warning"
                    data-testid="remote-stopped-environment"
                />
            )}

            <LocalRunnerSettingsCard localConfigRunner={localConfigRunner} readOnly={readOnly} />
        </div>
    );
};

type LocalRunnerSettingsCardProps = {
    localConfigRunner: PlainRunner;
    readOnly: boolean;
};

const LocalRunnerSettingsCard = ({ localConfigRunner, readOnly }: LocalRunnerSettingsCardProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const allProviders = useProviders();

    const schemas = useMemo(() => {
        // Only show PAT providers.
        return allProviders.filter((p) => p.schema.pat).map((p) => p.schema);
    }, [allProviders]);

    return (
        <div className="flex flex-col gap-4">
            <div className="rounded-xl border border-border-base">
                <div className="flex flex-col justify-between gap-4 p-6 lg:flex-row lg:items-center lg:gap-8">
                    <div className="flex min-w-0 items-center gap-4">
                        <IconLaptop size="xl" className="flex-shrink-0" />
                        <div className="flex min-w-0 flex-col gap-1">
                            <Text className="font-semibold">Ona Desktop Overview</Text>
                            <Text className="text-sm text-content-secondary">
                                {readOnly ? (
                                    "Organization admins can change these settings"
                                ) : (
                                    <span>
                                        Local environments can be{" "}
                                        <Link to="/settings/policies" className="text-content-accent underline">
                                            disabled in policies
                                        </Link>
                                    </span>
                                )}
                            </Text>
                        </div>
                    </div>
                    <RunnerUsageButtons
                        localRunner={true}
                        testIdPrefix="local-runner-settings"
                        className="flex flex-wrap items-center gap-4 lg:flex-nowrap"
                    />
                </div>
                <RepositoryAccessSettings
                    isExpanded={isExpanded}
                    setIsExpanded={setIsExpanded}
                    schemas={schemas}
                    localConfigRunner={localConfigRunner}
                />
            </div>
        </div>
    );
};

type RepositoryAccessSettingsProps = {
    isExpanded: boolean;
    setIsExpanded: (expanded: boolean) => void;
    schemas: RunnerConfigurationSchema_SCMConfigSchema[];
    localConfigRunner: PlainRunner;
};

const RepositoryAccessSettings: FC<RepositoryAccessSettingsProps> = ({
    isExpanded,
    setIsExpanded,
    schemas,
    localConfigRunner,
}) => {
    return (
        <div className="border-t border-border-base">
            <Collapsible open={isExpanded} className="flex flex-col gap-1">
                <CollapsibleTrigger asChild>
                    <Button
                        variant="ghost"
                        size="lg"
                        className="flex h-16 w-full items-center justify-between border-0 p-4 transition-transform hover:cursor-pointer hover:bg-transparent"
                        aria-label={isExpanded ? "Collapse" : "Expand"}
                        onClick={() => setIsExpanded(!isExpanded)}
                        data-tracking-id-none
                    >
                        <Text className="text-lg font-normal">Ona Desktop repository access settings</Text>
                        <IconChevronRight className={cn("mr-4", isExpanded && "rotate-90")} size={"base"} />
                    </Button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <div className="p-4 pt-0">
                        <SourceControlProvider
                            runner={localConfigRunner}
                            hasStack={true}
                            withDescription={false}
                            schemas={schemas}
                            Container={({ children }) => <div className="flex flex-col gap-4">{children}</div>}
                            data-track-location={(modalState) =>
                                modalState.type == "add-provider"
                                    ? TrackLocations.AddLocalSCMProviderModal
                                    : modalState.type == "remove-provider"
                                      ? TrackLocations.DeleteLocalSCMProviderModal
                                      : TrackLocations.EditLocalSCMProviderModal
                            }
                        />
                    </div>
                </CollapsibleContent>
            </Collapsible>
        </div>
    );
};
